import React, { useState, useEffect } from 'react';
import BacktestForm from './BacktestForm';
import BacktestChart from './BacktestChart';
import BacktestMetrics from './BacktestMetrics';
import portfolioService from '@/services/portfolioService';
import backtestService from '@/services/backtestService';
import { useToast } from '@/hooks/use-toast';
import { Card } from '@/components/ui/card';
import { Loader2 } from 'lucide-react';
import { useLocation } from 'react-router-dom';
import {
  calculatePortfolioNAV,
  calculateDailyReturns,
  calculateAnnualizedReturn,
  calculateVolatility,
  calculateSharpeRatio,
  calculateMaxDrawdown,
  calculateMonthlyStats,
  calculateBenchmarkReturns
} from '@/utils/backtestCalculations';

const Backtesting = () => {
  const [portfolios, setPortfolios] = useState([]);
  const [portfolioHoldings, setPortfolioHoldings] = useState([]);
  const [isLoadingPortfolio, setIsLoadingPortfolio] = useState(true);
  const [isRunning, setIsRunning] = useState(false);
  const [progress, setProgress] = useState(0);
  const [results, setResults] = useState(null);
  const [debugInfo, setDebugInfo] = useState([]);
  const { toast } = useToast();
  const location = useLocation();

  const addDebug = (message) => {
    console.log(message);
    setDebugInfo(prev => [...prev, `${new Date().toLocaleTimeString()}: ${message}`]);
  };

  useEffect(() => {
    loadPortfolios();
  }, []);

  // Check if stocks were passed from screening page
  useEffect(() => {
    if (location.state?.selectedStocks) {
      const tickers = location.state.selectedStocks;
      toast({
        title: 'Stocks Selected',
        description: `${tickers.length} stocks loaded from screening`,
      });
    }
  }, [location.state]);

  const loadPortfolios = async () => {
    try {
      setIsLoadingPortfolio(true);
      const data = await portfolioService.getAllPortfolios();
      setPortfolios(data);
      
      if (data.length > 0 && data[0].holdings) {
        setPortfolioHoldings(data[0].holdings);
      }
    } catch (error) {
      console.error('Error loading portfolios:', error);
      toast({
        title: 'Error',
        description: 'Failed to load portfolio data',
        variant: 'destructive',
      });
    } finally {
      setIsLoadingPortfolio(false);
    }
  };

  const runBacktest = async (config) => {
    try {
      setIsRunning(true);
      setProgress(0);
      setResults(null);
      setDebugInfo([]);
      addDebug('🚀 Starting backtest...');

      const { initialCapital, tickers, timeframe, rebalanceFrequency, weights } = config;

      // Step 1: Fetch historical data (40%)
      toast({
        title: 'Fetching Data',
        description: 'Loading historical prices...',
      });
      setProgress(20);

      const historicalData = await backtestService.getHistoricalPrices(tickers, timeframe);
      
      // Validate data
      const validTickers = tickers.filter(ticker => {
        const data = historicalData[ticker];
        return data && data.prices && data.prices.length > 0;
      });

      if (validTickers.length === 0) {
        throw new Error('No valid historical data found for selected stocks');
      }

      addDebug(`📊 Loaded ${validTickers.length} stocks with historical data`);

      if (validTickers.length === 0) {
        throw new Error('No valid historical data found for selected stocks');
      }

      setProgress(40);

      // Step 2: Fetch benchmark data (60%)
      toast({
        title: 'Loading Benchmark',
        description: 'Fetching S&P 500 data...',
      });

      const benchmarkData = await backtestService.getBenchmarkData(timeframe);
      setProgress(60);

      // Step 3: Calculate portfolio NAV (80%)
      toast({
        title: 'Calculating',
        description: 'Running backtest calculations...',
      });

      const { navSeries, drawdowns, finalValue} = calculatePortfolioNAV(
        historicalData,
        weights,
        initialCapital,
        rebalanceFrequency
      );

      addDebug(`📊 Backtest complete: $${initialCapital.toFixed(0)} → $${finalValue.toFixed(0)}`);

      if (navSeries.length === 0) {
        throw new Error('NAV calculation failed - no data points generated');
      }

      setProgress(80);

      // Step 4: Calculate metrics (100%)
      const totalReturn = ((finalValue - initialCapital) / initialCapital) * 100;
      
      // Calculate years for annualized return
      const startDate = new Date(navSeries[0].date);
      const endDate = new Date(navSeries[navSeries.length - 1].date);
      const years = (endDate - startDate) / (1000 * 60 * 60 * 24 * 365);
      
      const annualizedReturn = calculateAnnualizedReturn(initialCapital, finalValue, years);
      
      // Calculate returns for volatility
      const navValues = navSeries.map(item => item.value);
      const dailyReturns = calculateDailyReturns(navValues);
      
      const volatility = calculateVolatility(dailyReturns);
      const sharpeRatio = calculateSharpeRatio(annualizedReturn, volatility);
      const maxDrawdown = calculateMaxDrawdown(drawdowns);
      const { winRate, bestMonth, worstMonth } = calculateMonthlyStats(navSeries);
      
      addDebug(`✅ Return: ${totalReturn.toFixed(1)}% | Sharpe: ${sharpeRatio.toFixed(2)} | Volatility: ${volatility.toFixed(1)}%`);

      // Calculate benchmark returns
      const benchmarkReturns = calculateBenchmarkReturns(benchmarkData.prices, initialCapital);

      setProgress(100);

      // Validate metrics before setting results
      const metricsResult = {
        totalReturn: isNaN(totalReturn) ? 0 : totalReturn,
        annualizedReturn: isNaN(annualizedReturn) ? 0 : annualizedReturn,
        maxDrawdown: isNaN(maxDrawdown) ? 0 : maxDrawdown,
        sharpeRatio: isNaN(sharpeRatio) ? 0 : sharpeRatio,
        volatility: isNaN(volatility) ? 0 : volatility,
        winRate: isNaN(winRate) ? 0 : winRate,
        bestMonth: isNaN(bestMonth) ? 0 : bestMonth,
        worstMonth: isNaN(worstMonth) ? 0 : worstMonth,
      };

      // Set results
      setResults({
        navData: navSeries,
        benchmarkData: benchmarkReturns,
        drawdownData: drawdowns,
        metrics: metricsResult,
        config: {
          initialCapital,
          finalValue,
          tickers: validTickers,
          timeframe,
          rebalanceFrequency,
        }
      });

      toast({
        title: 'Success!',
        description: 'Backtest completed successfully',
      });

    } catch (error) {
      console.error('Error running backtest:', error);
      toast({
        title: 'Error',
        description: error.message || 'Failed to run backtest',
        variant: 'destructive',
      });
    } finally {
      setIsRunning(false);
      setProgress(0);
    }
  };

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-900 via-blue-900 to-slate-900">
      {/* Main Content */}
      <div className="container mx-auto px-6 py-8">
        {isLoadingPortfolio ? (
          <div className="flex items-center justify-center py-20">
            <Loader2 className="w-12 h-12 text-blue-400 animate-spin" />
          </div>
        ) : (
          <div className="space-y-8">
            {/* Backtest Form */}
            <BacktestForm
              portfolioHoldings={portfolioHoldings}
              onRunBacktest={runBacktest}
              isLoading={isRunning}
            />

            {/* Progress Bar */}
            {isRunning && (
              <Card className="p-6">
                <div className="flex items-center justify-between mb-2">
                  <span className="text-sm font-medium">Running Backtest...</span>
                  <span className="text-sm font-medium">{progress}%</span>
                </div>
                <div className="w-full bg-gray-200 rounded-full h-3 mb-4">
                  <div
                    className="bg-blue-600 h-3 rounded-full transition-all duration-300"
                    style={{ width: `${progress}%` }}
                  ></div>
                </div>
                {debugInfo.length > 0 && (
                  <div className="mt-4 max-h-48 overflow-y-auto bg-gray-50 dark:bg-gray-900 p-3 rounded text-xs font-mono">
                    {debugInfo.map((msg, idx) => (
                      <div key={idx} className="text-gray-700 dark:text-gray-300">{msg}</div>
                    ))}
                  </div>
                )}
              </Card>
            )}

            {/* Results */}
            {results && (
              <div className="space-y-8 animate-fadeIn">
                {/* Summary Card */}
                <Card className="p-6 bg-slate-800/95 border-slate-700 backdrop-blur-sm">
                  <h3 className="text-xl font-bold mb-4 text-white">Backtest Summary</h3>
                  <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                    <div>
                      <p className="text-sm text-slate-400">Initial Capital</p>
                      <p className="text-xl font-semibold text-white">
                        ${results.config.initialCapital.toLocaleString()}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-400">Final Value</p>
                      <p className="text-xl font-semibold text-blue-400">
                        ${results.config.finalValue.toLocaleString('en-US', {
                          minimumFractionDigits: 2,
                          maximumFractionDigits: 2
                        })}
                      </p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-400">Stocks</p>
                      <p className="text-xl font-semibold text-white">{results.config.tickers.join(', ')}</p>
                    </div>
                    <div>
                      <p className="text-sm text-slate-400">Period</p>
                      <p className="text-xl font-semibold text-white">
                        {results.config.timeframe.toUpperCase()}
                      </p>
                    </div>
                  </div>
                </Card>

                {/* Metrics */}
                <BacktestMetrics metrics={results.metrics} />

                {/* Chart */}
                <BacktestChart
                  navData={results.navData}
                  benchmarkData={results.benchmarkData}
                  drawdownData={results.drawdownData}
                />
              </div>
            )}
          </div>
        )}
      </div>
    </div>
  );
};

export default Backtesting;
